import { Box, Flex, useClipboard } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { useSnackbar } from 'ui-components';
import { ContentCaption } from '~/components/ContentCaption';
import {
  ContentDetailMetrics,
  ContentDetailMetricsContentType,
} from '~/components/ContentDetailMetrics';
import { PLATFORM_METRICS } from '~/components/ContentDetailMetrics/ContentDetailMetrics.constants';
import {
  Actions,
  Asset,
  AssetDescription,
  CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
  CONTENT_DETAIL_MODAL_TRACK_ID,
  ContentDetailModalTrackIdType,
} from '~/components/ContentDetailModal';
import { INSTAGRAM_CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { CreatorInfo } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import { LayoutContentSharedScreen } from '~/screens/ContentSharedScreen/components/LayoutContentSharedScreen';
import { IApiSearchInstagramContent } from '~/searchApi';

export interface ContentSharediScreenProps {
  creator: CreatorInfo;
  content: ContentDetailMetricsContentType;
  trackIds?: ContentDetailModalTrackIdType;
  isMetricsCollaborationFFEnbled?: boolean;
}

export function ContentSharedScreen({
  creator,
  content,
  trackIds: overrideDefaultTrackIds,
  isMetricsCollaborationFFEnbled,
}: ContentSharediScreenProps) {
  const trackIds = useMemo(
    () => ({
      ...CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
      ...overrideDefaultTrackIds,
    }),
    [overrideDefaultTrackIds],
  );
  const { onCopy } = useClipboard(content?.shareLink ?? '');

  const getMetrics = useCallback(() => {
    const actualPlatform = content?.platform;

    if (actualPlatform === SOCIAL_PLATFORMS.instagram) {
      const contentContainsViews =
        Object.keys(content as IApiSearchInstagramContent).some(
          (key) => key === 'viewsCount',
        ) && Boolean(content?.viewsCount);

      let metrics =
        PLATFORM_METRICS[(content as IApiSearchInstagramContent).channel];

      if (contentContainsViews) {
        if (
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.story ||
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.post
        ) {
          //@ts-ignore
          const { impressions: _impressions, ...rest } = metrics;
          metrics = rest;
        }

        if (
          (content as IApiSearchInstagramContent).channel ===
          INSTAGRAM_CONTENT_TYPES.reels
        ) {
          //@ts-ignore
          const {
            igReelsAggregatedAllPlaysCount: _igReelsAggregatedAllPlaysCount,
            ...rest
          } = metrics;
          metrics = rest;
        }
      }
      return metrics;
    }
    //@ts-ignore
    return PLATFORM_METRICS[actualPlatform];
  }, [content]);

  const snackbar = useSnackbar();

  const handleShare = useCallback(() => {
    onCopy();
    snackbar.success({
      description: 'Link successfully copied',
    });
  }, [onCopy, snackbar]);

  const isYoutubeContent = content?.platform === SOCIAL_PLATFORMS.youtube;

  return (
    <LayoutContentSharedScreen creator={creator}>
      <Flex
        minWidth={
          isYoutubeContent
            ? { base: 'auto', md: '50%' }
            : { base: 'auto', md: '280px' }
        }
        paddingRight={{ base: '0', md: '4' }}
        alignItems={'center'}
        flexDir={'column'}
      >
        <Box width={{ base: '70%', md: '100%' }}>
          <Asset
            content={content}
            trackId={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.preview]}
          />
          <AssetDescription content={content} trackIds={trackIds} />
        </Box>
      </Flex>
      <Box
        minWidth={
          isYoutubeContent
            ? { base: 'auto', md: '50%' }
            : { base: 'auto', md: '448px' }
        }
      >
        <ContentDetailMetrics
          marginBottom={2}
          content={content}
          metrics={getMetrics()}
          isMetricsCollaborationFFEnbled={isMetricsCollaborationFFEnbled}
        />
        <Actions
          content={content}
          creator={creator}
          onShare={handleShare}
          trackIds={trackIds}
        />
        <ContentCaption content={content} />
      </Box>
    </LayoutContentSharedScreen>
  );
}
